import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Star, TrendingUp, Check, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { generateTiers } from "@/lib/retailPricing/tiers";
import { MANUFACTURERS, type ManufacturerKey } from "@/lib/retailPricing/packages";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const TIER_META = {
  good: { icon: Check, label: "Good" },
  better: { icon: TrendingUp, label: "Better" },
  best: { icon: Star, label: "Best" },
} as const;

export default function RetailPreparePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { orgId } = useAuth();
  const customerName = params.get("name") || "";
  const customerEmail = params.get("email") || "";
  const customerPhone = params.get("phone") || "";
  const address = params.get("address") || "";

  const [squares, setSquares] = useState<number>(25);
  const [manufacturer, setManufacturer] = useState<ManufacturerKey>("gaf");
  const [selectedTier, setSelectedTier] = useState<"good" | "better" | "best">("better");
  const [creating, setCreating] = useState(false);

  const tiers = useMemo(() => generateTiers(manufacturer, { totalSquares: squares }), [manufacturer, squares]);

  const handleCreateProposal = async () => {
    if (!orgId) { toast.error("No organization found"); return; }
    setCreating(true);
    try {
      const tierList = (["good", "better", "best"] as const).map((k) => ({
        key: k,
        name: TIER_META[k].label,
        source: "custom",
        manufacturer: tiers[k].manufacturer,
        product_line: tiers[k].systemName,
        price: tiers[k].total,
        included: tiers[k].highlights,
        colors: [],
        highlight: k === "better",
      }));

      const { data, error } = await supabase
        .from("digital_proposals")
        .insert({
          org_id: orgId,
          title: `Retail Proposal — ${customerName || "Homeowner"}`,
          customer_name: customerName || "Homeowner",
          customer_email: customerEmail || null,
          customer_phone: customerPhone || null,
          property_address: address,
          tiers: tierList as any,
          selected_tier: selectedTier,
          scope_summary: `${tiers[selectedTier].systemName} — ${squares} squares`,
          financing_enabled: false,
          deposit_enabled: false,
          status: "draft",
        })
        .select("id, share_id")
        .single();

      if (error) throw error;
      toast.success("Proposal created");
      navigate(`/retail/proposal/${data.id}`);
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Failed to create proposal");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-10 bg-card border-b border-border/30 px-4 py-3 flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-sm font-bold text-foreground truncate">Good · Better · Best</h1>
          <p className="text-xs text-muted-foreground truncate">{address}</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <div className="rounded-xl border border-border/40 bg-card p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Roof Size (squares)</Label>
              <Input type="number" min={5} max={200} value={squares} onChange={(e) => setSquares(Number(e.target.value) || 0)} className="h-10" />
            </div>
            <div>
              <Label className="text-xs">Manufacturer</Label>
              <select value={manufacturer} onChange={(e) => setManufacturer(e.target.value as ManufacturerKey)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
                {MANUFACTURERS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {(["good", "better", "best"] as const).map((tier) => {
            const t = tiers[tier];
            const Icon = TIER_META[tier].icon;
            const isSelected = selectedTier === tier;
            return (
              <motion.button
                key={tier}
                whileTap={{ scale: 0.98 }}
                onClick={() => setSelectedTier(tier)}
                className={cn(
                  "rounded-xl border-2 p-4 text-left transition-all",
                  isSelected ? "border-primary bg-primary/5 shadow-lg" : "border-border/40 bg-card hover:border-border",
                )}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-primary" />
                    <span className="text-sm font-bold">{TIER_META[tier].label}</span>
                  </div>
                  {tier === "better" && <span className="text-[10px] font-bold uppercase text-primary">Recommended</span>}
                </div>
                <p className="text-2xl font-extrabold text-foreground">${t.total.toLocaleString()}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">~${Math.round(t.total / 60)}/mo</p>
                <p className="text-xs font-semibold text-foreground mt-3">{t.systemName}</p>
                <p className="text-[11px] text-muted-foreground">{t.warrantyName}</p>
                <ul className="mt-3 space-y-1">
                  {t.highlights.slice(0, 4).map((h, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-[11px] text-foreground/80">
                      <Check className="h-3 w-3 text-primary mt-0.5 flex-shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </motion.button>
            );
          })}
        </div>

        <Button className="h-14 w-full gap-2 rounded-xl text-base font-bold" onClick={handleCreateProposal} disabled={creating}>
          {creating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
          Create Proposal & Get Share Link
        </Button>
      </div>
    </div>
  );
}
